import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Truck, Pencil, FileText, Eye, Upload, Download, ChevronLeft, ChevronRight, Search as SearchIcon, Check as CheckIcon, RefreshCw, Package } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fmtDate } from '@/lib/dateFmt';
import { getFileUrl, isPdf, isImage } from '@/utils/fileUrl';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionToolbar } from '@/components/BulkActionToolbar';
import { AccontoSelector } from '@/components/AccontoSelector';
import { SafeTableBoundary } from '@/components/SafeTableBoundary';
import { DDTDaMagazzinoDialog } from '@/components/DDTDaMagazzinoDialog';
import { useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const CAUSALI = [
  { value: 'acquisto', label: 'Acquisto materiali' },
  { value: 'trasporto', label: 'Trasporto' },
  { value: 'reso', label: 'Reso al fornitore' },
  { value: 'spostamento', label: 'Spostamento merci' },
  { value: 'altro', label: 'Altro' },
];

const EMPTY_RIGA = { descrizione: '', quantita: '', unita_misura: 'pz', prezzo_unitario: '', importo: '' };

// Ritorna l'ultimo giorno del mese della data ISO fornita (YYYY-MM-DD).
// Es: 2026-06-12 → 2026-06-30. Usato per auto-impostare data_fatturazione_prevista.
function _lastDayOfMonthISO(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m] = iso.split('-').map(Number);
  // new Date(y, m, 0) → ultimo giorno del mese m (m è 1-based qui)
  const last = new Date(y, m, 0).getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

const EMPTY_FORM = {
  numero: '',
  data: new Date().toISOString().split('T')[0],
  fornitore: '',
  fornitore_piva: '',
  fornitore_id: '',
  cantiere_id: '',
  descrizione: '',  // facoltativa: se vuota viene composta dalle righe
  righe: [],
  costo_netto: 0,
  iva_pct: 22,
  causale: 'acquisto',
  note: '',
  file_url: '',
  data_fatturazione_prevista: '',
  data_pagamento_prevista: '',
  stato_fatturazione: 'da_fatturare',
  modalita_pagamento_id: '',
  pagamento_formula: '',
  pagamento_scadenze_multiple: [],
  acconto_riferimento: '',
};

export default function DDTPage() {
  const [ddt, setDdt] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filterCantiere, setFilterCantiere] = useState('all');
  // Filtro mensile (default = mese corrente; null = mostra tutti)
  const _now = new Date();
  const [anno, setAnno] = useState(_now.getFullYear());
  const [mese, setMese] = useState(_now.getMonth() + 1);
  const [showAllMonths, setShowAllMonths] = useState(false);
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDdt, setPreviewDdt] = useState(null);
  // Upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  // Bulk-selection state for mass-delete
  // Fatturazione prevista (raggruppata per fornitore × data)
  const [fattPrevista, setFattPrevista] = useState({ groups: [], totale_complessivo: 0 });
  // Anagrafica fornitori per autocomplete
  const [fornitoriList, setFornitoriList] = useState([]);
  // Modalità di pagamento per auto-calcolo scadenza
  const [modalitaList, setModalitaList] = useState([]);
  // Combobox cantiere
  const [cantPopoverOpen, setCantPopoverOpen] = useState(false);

  // iter100k: DDT da Magazzino
  const [magDialogOpen, setMagDialogOpen] = useState(false);
  const [magPendingCount, setMagPendingCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchMagPending = async () => {
    try {
      const r = await axios.get(`${API}/magazzino/movimenti/ddt-pending`, { withCredentials: true });
      setMagPendingCount((r.data?.items || []).filter(i => i.ddt_status !== 'created').length);
    } catch { /* silenzioso */ }
  };

  useEffect(() => {
    fetchMagPending();
    // deep-link: /admin/ddt?tab=da-magazzino → apre subito il dialog
    if (searchParams.get('tab') === 'da-magazzino') {
      setMagDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    fetchFatturazionePrevista();
    fetchFornitori();
    fetchModalita();
  }, []);

  const fetchModalita = async () => {
    try {
      const r = await axios.get(`${API}/modalita-pagamento`, { withCredentials: true });
      setModalitaList(r.data || []);
    } catch { /* non bloccante */ }
  };

  // Raggruppa cantieri per client_name (Combobox)
  const cantieriGrouped = useMemo(() => {
    const groups = new Map();
    (cantieri || []).forEach(c => {
      // iter86-t: coercizione a stringa per evitare crash su valori numerici
      const key = String(c.client_name || c.cliente || 'Senza cliente').trim() || 'Senza cliente';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b, 'it'))
      .map(([cliente, list]) => ({
        cliente,
        cantieri: list.sort((x, y) => (x.name || '').localeCompare(y.name || '', 'it')),
      }));
  }, [cantieri]);

  const selectedCantiere = useMemo(() => cantieri.find(c => c.id === form.cantiere_id), [cantieri, form.cantiere_id]);

  // Auto-ricalcolo scadenza pagamento quando cambia data DDT / data fattura / modalità / fornitore
  const recalcScadenza = async (override = {}) => {
    const dataRif = override.data_fatturazione_prevista || form.data_fatturazione_prevista || override.data || form.data;
    if (!dataRif) return;
    // Trova modalità: prima quella scelta nel form, poi quella del fornitore
    let modalita_id = override.modalita_pagamento_id ?? form.modalita_pagamento_id;
    if (!modalita_id && form.fornitore_id) {
      const forn = fornitoriList.find(f => f.id === form.fornitore_id);
      modalita_id = forn?.modalita_pagamento_id || '';
    }
    if (!modalita_id) return;
    try {
      const r = await axios.post(`${API}/modalita-pagamento/calcola`, {
        data_fattura: dataRif,
        modalita_id,
      }, { withCredentials: true });
      if (r.data?.scadenza) {
        setForm(prev => ({
          ...prev,
          data_pagamento_prevista: r.data.scadenza,
          pagamento_formula: r.data.formula_applicata || '',
          modalita_pagamento_id: modalita_id,
        }));
      }
    } catch { /* silent */ }
  };

  const fetchFatturazionePrevista = async () => {
    try {
      const r = await axios.get(`${API}/ddt/fatturazione-prevista`, { withCredentials: true });
      setFattPrevista(r.data || { groups: [], totale_complessivo: 0 });
    } catch { /* non bloccante */ }
  };

  const fetchFornitori = async () => {
    try {
      const r = await axios.get(`${API}/fornitori`, { withCredentials: true });
      setFornitoriList(r.data || []);
    } catch { /* non bloccante */ }
  };

  const handleMarkGroupFatturato = async (g) => {
    if (!window.confirm(`Marcare come "fatturato" tutti gli ${g.n_ddt} DDT di ${g.fornitore} del ${g.data_fatturazione_prevista}?\nTotale: € ${g.totale_atteso.toFixed(2)}`)) return;
    try {
      await Promise.all((g.ddt || []).map(d => axios.post(`${API}/ddt/${d.id}/mark-fatturato`, {}, { withCredentials: true })));
      toast.success(`${g.n_ddt} DDT marcati come fatturati`);
      fetchData();
      fetchFatturazionePrevista();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ddtRes, cantRes] = await Promise.all([
        axios.get(`${API}/ddt`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true }),
      ]);
      setDdt(ddtRes.data || []);
      setCantieri(cantRes.data || []);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      numero: d.numero || '',
      data: d.data || '',
      fornitore: d.fornitore || '',
      fornitore_piva: d.fornitore_piva || '',
      fornitore_id: d.fornitore_id || '',
      cantiere_id: d.cantiere_id || '',
      descrizione: d.descrizione || '',
      righe: Array.isArray(d.righe) ? d.righe.map(r => ({
        descrizione: r.descrizione || '', quantita: r.quantita || '', unita_misura: r.unita_misura || 'pz',
        prezzo_unitario: r.prezzo_unitario || '', importo: r.importo || '',
      })) : [],
      costo_netto: d.costo_netto || 0,
      iva_pct: d.iva_pct || 22,
      causale: d.causale || 'acquisto',
      note: d.note || '',
      file_url: d.file_url || '',
      data_fatturazione_prevista: d.data_fatturazione_prevista || '',
      data_pagamento_prevista: d.data_pagamento_prevista || '',
      stato_fatturazione: d.stato_fatturazione || 'da_fatturare',
      modalita_pagamento_id: d.modalita_pagamento_id || '',
      pagamento_formula: d.pagamento_formula || '',
      pagamento_scadenze_multiple: d.pagamento_scadenze_multiple || [],
      acconto_riferimento: d.acconto_riferimento || '',
    });
    setDialogOpen(true);
  };

  // Helpers per gestire le righe materiale dinamiche
  const addRiga = () => setForm(prev => ({ ...prev, righe: [...prev.righe, { ...EMPTY_RIGA }] }));
  const removeRiga = (idx) => setForm(prev => ({ ...prev, righe: prev.righe.filter((_, i) => i !== idx) }));
  const updateRiga = (idx, field, value) => setForm(prev => ({
    ...prev,
    righe: prev.righe.map((r, i) => i === idx ? { ...r, [field]: value } : r),
  }));

  // Totale calcolato dalle righe (se presenti)
  const totaleDaRighe = form.righe.reduce((sum, r) => {
    const imp = parseFloat(r.importo) || 0;
    const calc = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
    return sum + (imp || calc);
  }, 0);

  const openPreview = (d) => {
    setPreviewDdt(d);
    setPreviewOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const filePath = res.data?.path;
      if (!filePath) throw new Error('Upload fallito');
      setForm(prev => ({ ...prev, file_url: `/api/files/${filePath}` }));
      toast.success('File caricato');
    } catch (err) {
      toast.error('Errore nel caricamento del file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.numero.trim()) { toast.error('Numero DDT obbligatorio'); return; }
    if (!form.fornitore.trim()) { toast.error('Fornitore obbligatorio'); return; }
    if (!form.cantiere_id) { toast.error('Seleziona un cantiere'); return; }
    if (!form.acconto_riferimento) { toast.error('Indica il riferimento: Acconto n° oppure Extra'); return; }
    try {
      const righePayload = form.righe.map(r => ({
        descrizione: r.descrizione || '',
        quantita: parseFloat(r.quantita) || 0,
        unita_misura: r.unita_misura || 'pz',
        prezzo_unitario: parseFloat(r.prezzo_unitario) || 0,
        importo: parseFloat(r.importo) || 0,
      })).filter(r => r.descrizione.trim() || r.importo > 0 || r.quantita > 0);
      const payload = {
        ...form,
        righe: righePayload,
        costo_netto: parseFloat(form.costo_netto) || 0,
        iva_pct: parseFloat(form.iva_pct) || 22,
      };
      if (editingId) {
        await axios.put(`${API}/ddt/${editingId}`, payload, { withCredentials: true });
        toast.success('DDT aggiornato — Contabilità sincronizzata');
      } else {
        await axios.post(`${API}/ddt`, payload, { withCredentials: true });
        toast.success('DDT registrato — Salvato nella contabilità del cantiere');
      }
      setDialogOpen(false);
      fetchData();
      fetchFatturazionePrevista();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Eliminare il DDT ${d.numero}? Verrà rimossa anche la spesa associata dal cantiere.`)) return;
    try {
      await axios.delete(`${API}/ddt/${d.id}`, { withCredentials: true });
      toast.success('DDT eliminato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const cambiaMese = (delta) => {
    let nuovoMese = mese + delta;
    let nuovoAnno = anno;
    if (nuovoMese > 12) { nuovoMese = 1; nuovoAnno += 1; }
    else if (nuovoMese < 1) { nuovoMese = 12; nuovoAnno -= 1; }
    setMese(nuovoMese);
    setAnno(nuovoAnno);
    setShowAllMonths(false);
  };

  const filtered = useMemo(() => {
    let list = filterCantiere === 'all' ? ddt : ddt.filter(d => d.cantiere_id === filterCantiere);
    if (!showAllMonths) {
      const monthPrefix = `${anno}-${String(mese).padStart(2, '0')}`;
      // iter86-t: coercizione difensiva — se `d.data` è number (import legacy),
      // `(1234 || '').startsWith(...)` crash → forziamo a stringa.
      list = list.filter(d => String(d.data == null ? '' : d.data).startsWith(monthPrefix));
    }
    return list;
  }, [ddt, filterCantiere, anno, mese, showAllMonths]);

  const total = useMemo(() => filtered.reduce((sum, d) => sum + (parseFloat(d.costo_netto) || 0), 0), [filtered]);
  const totalLordo = useMemo(() => filtered.reduce((sum, d) => sum + (parseFloat(d.costo_netto) || 0) * (1 + (parseFloat(d.iva_pct) || 22) / 100), 0), [filtered]);

  // Bulk-selection (declared before any early-return to satisfy rules-of-hooks)
  const bulkSel = useBulkSelection(filtered, (d) => d.id);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ddt-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Truck className="h-5 w-5" />DDT - Documenti di Trasporto
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Registra i DDT — vengono salvati automaticamente nella contabilità del cantiere</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-ddt-btn">
          <Plus className="h-4 w-4 mr-1.5" />Nuovo DDT
        </Button>
      </div>

      {/* Sempre visibile: accesso a "DDT da Magazzino" con badge */}
      <div className="-mt-1">
        <Button variant="outline" size="sm" onClick={() => setMagDialogOpen(true)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="ddt-mag-open-btn">
          <Package className="h-4 w-4 mr-1.5" />DDT da Magazzino
          {magPendingCount > 0 && <Badge className="ml-2 bg-amber-500 text-white">{magPendingCount}</Badge>}
        </Button>
      </div>

      {/* iter100k: banner DDT da Magazzino (visibile solo se ci sono pending) */}
      {magPendingCount > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="ddt-mag-banner">
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">
                {magPendingCount}
              </div>
              <div>
                <div className="text-sm font-semibold text-amber-900">Scarichi da magazzino in attesa di DDT</div>
                <div className="text-xs text-amber-800">Gli operai hanno scaricato materiali verso cantieri — imposta il prezzo di vendita e genera il DDT.</div>
              </div>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setMagDialogOpen(true)} data-testid="open-ddt-mag-dialog">
              <Truck className="h-4 w-4 mr-1.5" />Crea DDT da Magazzino
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Mese navigation — pattern Presenze */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" onClick={() => cambiaMese(-1)} disabled={showAllMonths} data-testid="ddt-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="flex flex-col items-center">
            <h2 className="text-base sm:text-lg font-bold capitalize" data-testid="ddt-current-month">
              {showAllMonths ? 'Tutti i mesi' : `${MESI[mese]} ${anno}`}
            </h2>
            <button
              type="button"
              onClick={() => setShowAllMonths(v => !v)}
              className="text-[11px] text-blue-600 hover:text-blue-800 underline"
              data-testid="ddt-toggle-all-months"
            >
              {showAllMonths ? 'Filtra per mese corrente' : 'Mostra tutti i mesi'}
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => cambiaMese(+1)} disabled={showAllMonths} data-testid="ddt-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">DDT del periodo</p>
            <p className="text-2xl font-bold text-slate-900">{filtered.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Totale Netto / Lordo</p>
            <p className="text-lg font-bold text-slate-900 leading-tight">€ {total.toFixed(2)}</p>
            <p className="text-sm text-slate-600 leading-tight">€ {totalLordo.toFixed(2)} <span className="text-[10px] text-slate-400">IVA inc.</span></p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex flex-col gap-1">
            <Label className="text-xs text-slate-500 uppercase">Filtra per cantiere</Label>
            <Select value={filterCantiere} onValueChange={setFilterCantiere}>
              <SelectTrigger className="h-9" data-testid="filter-cantiere-ddt"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <BulkActionToolbar
            resource="ddt"
            selectedIds={bulkSel.selectedIds}
            onClear={bulkSel.clear}
            onDeleted={fetchData}
            itemLabel="DDT"
          />
          <SafeTableBoundary label="dei DDT">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 pl-3">
                  <Checkbox
                    checked={bulkSel.isAllSelected}
                    onCheckedChange={bulkSel.toggleAll}
                    aria-label="Seleziona tutti i DDT"
                    data-testid="bulk-select-all-ddt"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase">Numero</TableHead>
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Fornitore</TableHead>
                <TableHead className="text-xs uppercase">Cantiere</TableHead>
                <TableHead className="text-xs uppercase">Causale</TableHead>
                <TableHead className="text-xs uppercase">Scadenze</TableHead>
                <TableHead className="text-xs uppercase">Stato</TableHead>
                <TableHead className="text-xs uppercase text-right">Netto</TableHead>
                <TableHead className="text-xs uppercase text-right">Lordo (IVA inc.)</TableHead>
                <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-10">
                  <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  Nessun DDT registrato — clicca &quot;Nuovo DDT&quot; per iniziare
                </TableCell></TableRow>
              ) : filtered.map(d => (
                <TableRow key={d.id} className="border-slate-100" data-testid={`ddt-row-${d.id}`} data-selected={bulkSel.isSelected(d.id) ? 'true' : 'false'}>
                  <TableCell className="pl-3">
                    <Checkbox
                      checked={bulkSel.isSelected(d.id)}
                      onCheckedChange={() => bulkSel.toggle(d.id)}
                      aria-label={`Seleziona DDT ${String(d.numero ?? '')}`}
                      data-testid={`bulk-select-ddt-${d.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{String(d.numero ?? '')}</TableCell>
                  <TableCell>{fmtDate(d.data, 'dd-MM-yyyy')}</TableCell>
                  <TableCell className="font-medium">
                    {String(d.fornitore ?? '')}
                    {d.fornitore_piva && (
                      <Link to={`/admin/fornitori?piva=${encodeURIComponent(String(d.fornitore_piva))}`} className="block text-[10px] text-blue-600 hover:text-blue-800 underline font-mono mt-0.5" title="Apri scheda fornitore">
                        {String(d.fornitore_piva)}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.cantiere_name ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{String(d.cantiere_name)}</Badge> : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell><span className="text-xs text-slate-600">{(CAUSALI.find(c => c.value === d.causale) || {}).label || String(d.causale ?? '')}</span></TableCell>
                  <TableCell className="text-[11px] leading-tight text-slate-600">
                    {d.data_fatturazione_prevista && <div>Fatt: <b className="text-slate-800">{fmtDate(d.data_fatturazione_prevista, 'dd-MM-yyyy')}</b></div>}
                    {d.data_pagamento_prevista && <div>Pag: <b className="text-slate-800">{fmtDate(d.data_pagamento_prevista, 'dd-MM-yyyy')}</b></div>}
                    {!d.data_fatturazione_prevista && !d.data_pagamento_prevista && <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell>
                    {d.stato_fatturazione === 'fatturato'
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Fatturato</Badge>
                      : <Badge variant="outline" className="border-amber-300 text-amber-700">Da fatturare</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-medium">€ {(parseFloat(d.costo_netto) || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium text-slate-900">€ {((parseFloat(d.costo_netto) || 0) * (1 + (parseFloat(d.iva_pct) || 22) / 100)).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => openPreview(d)} data-testid={`preview-ddt-${d.id}`} title="Anteprima">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(d)} data-testid={`edit-ddt-${d.id}`} title="Modifica">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(d)} data-testid={`delete-ddt-${d.id}`} title="Elimina">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </SafeTableBoundary>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica DDT' : 'Nuovo DDT'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Numero DDT *</Label>
                <Input value={form.numero} onChange={e => setForm({...form, numero: e.target.value})} required data-testid="ddt-numero" />
              </div>
              <div className="space-y-1">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={e => {
                    const v = e.target.value;
                    const eom = _lastDayOfMonthISO(v);
                    // Auto-imposta data_fatturazione_prevista = fine mese della data DDT
                    // (regola contabile: tutti i DDT di un mese vengono fatturati a fine mese).
                    // L'utente può comunque sovrascrivere manualmente nel campo dedicato.
                    setForm(prev => ({
                      ...prev,
                      data: v,
                      data_fatturazione_prevista: eom || prev.data_fatturazione_prevista,
                    }));
                    // Ricalcola la scadenza usando la nuova data fatturazione = fine mese
                    if (form.modalita_pagamento_id && eom) {
                      setTimeout(() => recalcScadenza({ data_fatturazione_prevista: eom }), 0);
                    }
                  }}
                  required
                  data-testid="ddt-data"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Fornitore * <span className="text-xs text-slate-400">(autocomplete da Anagrafica fornitori)</span></Label>
              <Input
                list="dl-fornitori"
                value={form.fornitore}
                onChange={e => {
                  const v = e.target.value;
                  // Se l'utente seleziona un fornitore dal datalist, auto-applica P.IVA + modalità
                  const match = fornitoriList.find(f => f.ragione_sociale === v);
                  const newModalitaId = match?.modalita_pagamento_id || '';
                  setForm(prev => ({
                    ...prev,
                    fornitore: v,
                    fornitore_piva: match?.partita_iva || prev.fornitore_piva || '',
                    fornitore_id: match?.id || '',
                    modalita_pagamento_id: newModalitaId || prev.modalita_pagamento_id,
                  }));
                  // Auto-ricalcola scadenza se il fornitore ha una modalità predefinita
                  if (newModalitaId) {
                    setTimeout(() => recalcScadenza({ modalita_pagamento_id: newModalitaId }), 0);
                  }
                }}
                required
                data-testid="ddt-fornitore"
                placeholder="Inizia a digitare per cercare…"
              />
              <datalist id="dl-fornitori">
                {fornitoriList.map(f => (
                  <option key={f.id} value={f.ragione_sociale}>{f.partita_iva ? `P.IVA ${f.partita_iva}` : ''}</option>
                ))}
              </datalist>
              {form.fornitore_piva && (
                <p className="text-[11px] text-emerald-700">P.IVA: <span className="font-mono">{form.fornitore_piva}</span></p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Cantiere *</Label>
              <Popover open={cantPopoverOpen} onOpenChange={setCantPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={cantPopoverOpen}
                    className="w-full justify-between font-normal"
                    data-testid="ddt-cantiere"
                  >
                    <span className={selectedCantiere ? '' : 'text-slate-500'}>
                      {selectedCantiere
                        ? `${selectedCantiere.name}${selectedCantiere.client_name ? ` — ${selectedCantiere.client_name}` : ''}`
                        : 'Seleziona cantiere...'}
                    </span>
                    <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[480px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cerca per nome cantiere o cliente…" data-testid="ddt-cantiere-search" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>Nessun cantiere trovato.</CommandEmpty>
                      {cantieriGrouped.map(group => (
                        <CommandGroup key={group.cliente} heading={group.cliente}>
                          {group.cantieri.map(c => (
                            <CommandItem
                              key={c.id}
                              value={`${c.name} ${group.cliente}`}
                              onSelect={() => {
                                setForm({ ...form, cantiere_id: c.id });
                                setCantPopoverOpen(false);
                              }}
                              data-testid={`ddt-cantiere-opt-${c.id}`}
                            >
                              <CheckIcon className={`mr-2 h-4 w-4 ${form.cantiere_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-400 mt-1">Il DDT verrà salvato nella contabilità di questo cantiere</p>
            </div>
            <AccontoSelector value={form.acconto_riferimento} onChange={v => setForm(prev => ({ ...prev, acconto_riferimento: v }))} hint="Obbligatorio: assegna il DDT a un acconto oppure segnalalo come Extra (lavori fuori preventivo)" dataTestId="ddt-acconto-select" />
            <div className="space-y-1">
              <Label>Causale</Label>
              <Select value={form.causale} onValueChange={v => setForm({...form, causale: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CAUSALI.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 border border-slate-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <Label className="m-0">Righe materiale <span className="text-xs text-slate-400">(opzionale, puoi aggiungerne quante vuoi)</span></Label>
                <Button type="button" variant="outline" size="sm" onClick={addRiga} data-testid="ddt-add-riga"><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi riga</Button>
              </div>
              {form.righe.length === 0 && (
                <p className="text-xs text-slate-400 italic">Nessuna riga — puoi semplicemente compilare descrizione + importo in basso, oppure cliccare "Aggiungi riga" per voci multiple.</p>
              )}
              {form.righe.map((r, idx) => (
                <div key={`riga-${idx}`} className="grid grid-cols-12 gap-2 items-end p-2 bg-slate-50 rounded">
                  <div className="col-span-5">
                    <Label className="text-xs">Descrizione</Label>
                    <Input value={r.descrizione} onChange={(e) => updateRiga(idx, 'descrizione', e.target.value)} placeholder="es. Cavo FG7 2.5mm²" data-testid={`riga-desc-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Qtà</Label>
                    <Input type="number" step="0.01" value={r.quantita} onChange={(e) => updateRiga(idx, 'quantita', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">UM</Label>
                    <Input value={r.unita_misura} onChange={(e) => updateRiga(idx, 'unita_misura', e.target.value)} placeholder="pz" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">€/unità</Label>
                    <Input type="number" step="0.01" value={r.prezzo_unitario} onChange={(e) => updateRiga(idx, 'prezzo_unitario', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Totale</Label>
                    <Input type="number" step="0.01" value={r.importo} onChange={(e) => updateRiga(idx, 'importo', e.target.value)} placeholder="auto" title="Lascia vuoto per calcolo automatico" />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeRiga(idx)} data-testid={`riga-remove-${idx}`} title="Rimuovi riga"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
              {form.righe.length > 0 && (
                <div className="text-right text-sm text-slate-600 pt-1">Totale righe: <b className="text-slate-900">€ {totaleDaRighe.toFixed(2)}</b></div>
              )}
            </div>

            <div className="space-y-1">
              <Label>Descrizione generale <span className="text-xs text-slate-400">(opzionale, vince sulle righe per la stampa)</span></Label>
              <Textarea rows={2} value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} placeholder="Lasciato vuoto, viene composto dalle righe" data-testid="ddt-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Importo netto totale (€) <span className="text-xs text-slate-400">{form.righe.length > 0 ? '— auto da righe' : ''}</span></Label>
                <Input type="number" step="0.01" value={form.righe.length > 0 ? totaleDaRighe.toFixed(2) : form.costo_netto} onChange={e => setForm({...form, costo_netto: e.target.value})} readOnly={form.righe.length > 0} data-testid="ddt-costo" />
              </div>
              <div className="space-y-1">
                <Label>IVA (%)</Label>
                <Input type="number" step="0.01" value={form.iva_pct} onChange={e => setForm({...form, iva_pct: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <div className="space-y-1">
                <Label>Data fatturazione prevista <span className="text-xs text-slate-400">(opzionale)</span></Label>
                <Input
                  type="date"
                  value={form.data_fatturazione_prevista}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(prev => ({...prev, data_fatturazione_prevista: v}));
                    if (form.modalita_pagamento_id) recalcScadenza({ data_fatturazione_prevista: v });
                  }}
                  data-testid="ddt-data-fatt"
                />
                <p className="text-[11px] text-slate-400">Quando il fornitore emetterà la fattura</p>
              </div>
              <div className="space-y-1">
                <Label>Data pagamento prevista <span className="text-xs text-slate-400">(opzionale)</span></Label>
                <Input type="date" value={form.data_pagamento_prevista} onChange={e => setForm({...form, data_pagamento_prevista: e.target.value})} data-testid="ddt-data-pag" />
                <p className="text-[11px] text-slate-400">Scadenza pagamento attesa</p>
              </div>
            </div>
            <div className="space-y-1 bg-blue-50/40 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm font-semibold text-blue-900">Modalità di pagamento</Label>
                {form.modalita_pagamento_id && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[11px] text-blue-700 hover:text-blue-900"
                    onClick={() => recalcScadenza({})}
                    data-testid="ddt-ricalcola-scadenza"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Ricalcola
                  </Button>
                )}
              </div>
              <Select
                value={form.modalita_pagamento_id || 'none'}
                onValueChange={(v) => {
                  const newId = v === 'none' ? '' : v;
                  setForm(prev => ({...prev, modalita_pagamento_id: newId}));
                  if (newId) recalcScadenza({ modalita_pagamento_id: newId });
                  else setForm(prev => ({...prev, modalita_pagamento_id: '', pagamento_formula: ''}));
                }}
              >
                <SelectTrigger data-testid="ddt-modalita-select"><SelectValue placeholder="Auto (da anagrafica fornitore)" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Auto (da anagrafica fornitore)</SelectItem>
                  {modalitaList.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.pagamento_formula && (
                <p className="text-[11px] text-blue-700 mt-1" data-testid="ddt-formula-label">
                  Formula applicata: <span className="font-mono font-medium">{form.pagamento_formula}</span>
                </p>
              )}
              {Array.isArray(form.pagamento_scadenze_multiple) && form.pagamento_scadenze_multiple.length > 1 && (
                <div className="flex gap-1 flex-wrap mt-1">
                  {form.pagamento_scadenze_multiple.map((s, i) => (
                    <Badge key={s} variant="outline" className="font-mono text-[10px] bg-white">
                      Rata {i + 1}: {fmtDate(s, 'dd-MM-yyyy')}
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-500">La scadenza pagamento viene compilata automaticamente in base alla formula scelta. Se lasci "Auto" sarà quella predefinita del fornitore.</p>
            </div>
            <div className="space-y-1">
              <Label>Note interne</Label>
              <Textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})} />
            </div>
            <div className="space-y-1 border-t pt-3">
              <Label>File DDT (PDF, immagine — opzionale)</Label>
              {form.file_url ? (
                <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                    <span className="text-sm text-blue-900 truncate">File caricato</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => setForm({...form, file_url: ''})} data-testid="remove-ddt-file">Rimuovi</Button>
                </div>
              ) : (
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  data-testid="ddt-file-input"
                />
              )}
              {uploadingFile && <p className="text-xs text-slate-500">Caricamento in corso...</p>}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white" data-testid="submit-ddt">{editingId ? 'Salva modifiche' : 'Registra DDT'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog - view only, no edit */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[92vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-5 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-slate-700" />
              DDT {previewDdt?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {previewDdt && (
              <>
                {/* Info card */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Numero</p><p className="font-mono font-medium">{previewDdt.numero}</p></div>
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Data</p><p className="font-medium">{fmtDate(previewDdt.data, 'dd MMM yyyy')}</p></div>
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Fornitore</p><p className="font-medium">{previewDdt.fornitore}</p></div>
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Cantiere</p><p className="font-medium">{previewDdt.cantiere_name || '-'}</p></div>
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Causale</p><p className="font-medium">{(CAUSALI.find(c => c.value === previewDdt.causale) || {}).label || previewDdt.causale || '-'}</p></div>
                  <div className="space-y-0.5"><p className="text-xs text-slate-500 uppercase">Importo netto</p><p className="font-bold text-slate-900">€ {(parseFloat(previewDdt.costo_netto) || 0).toFixed(2)}</p></div>
                </div>
                {previewDdt.descrizione && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-slate-500 uppercase mb-1">Descrizione materiali</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{previewDdt.descrizione}</p>
                  </div>
                )}
                {previewDdt.note && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-slate-500 uppercase mb-1">Note interne</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{previewDdt.note}</p>
                  </div>
                )}
                {/* File preview */}
                {previewDdt.file_url ? (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500 uppercase">File DDT</p>
                      <a href={getFileUrl(previewDdt.file_url)} target="_blank" rel="noopener noreferrer" download>
                        <Button size="sm" variant="outline" data-testid="download-ddt-file"><Download className="h-3.5 w-3.5 mr-1" />Scarica</Button>
                      </a>
                    </div>
                    <div className="bg-slate-100 rounded border border-slate-200 overflow-hidden" style={{ minHeight: '400px' }}>
                      {(() => {
                        if (isImage(previewDdt.file_url)) {
                          return <img src={getFileUrl(previewDdt.file_url)} alt="DDT" className="max-w-full h-auto mx-auto block" />;
                        }
                        if (isPdf(previewDdt.file_url)) {
                          return <iframe src={getFileUrl(previewDdt.file_url)} className="w-full h-[480px] border-0" title="Anteprima DDT" />;
                        }
                        return (
                          <div className="p-8 text-center">
                            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm text-slate-600">Anteprima non disponibile per questo formato</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="border-t pt-3">
                    <p className="text-xs text-slate-400 italic">Nessun file allegato — usa "Modifica" per caricare un PDF/immagine</p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t bg-white">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Chiudi</Button>
            <Button className="bg-slate-900 text-white" onClick={() => { setPreviewOpen(false); previewDdt && openEdit(previewDdt); }} data-testid="edit-from-preview">
              <Pencil className="h-4 w-4 mr-1.5" />Modifica
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter100k: dialog DDT da Magazzino */}
      <DDTDaMagazzinoDialog
        open={magDialogOpen}
        onOpenChange={setMagDialogOpen}
        onCreated={() => { fetchData(); fetchMagPending(); }}
      />
    </div>
  );
}
